/**
 * Jobs Palletways deleted or closed without carrying them: labelled in the Job
 * table's Vehicle column and left out of totals. Mirrors zeecore
 * src/projections/profitability/orderCancellation.ts (which keeps them out of
 * the £/job fill and network cost) — keep the two in step.
 *
 * DELETED is always cancelled; JOB CLOSED only when no vehicle carried the job.
 */
export type CancelledOrderStatus = 'DELETED' | 'JOB CLOSED';

/**
 * A (not cancelled) job with no vehicle shows its Palletways status in the
 * Vehicle cell, so users see exactly where it is: "Entered", "In Collection
 * Depot", "Trunked To Hub"… `gap` marks a job that is done but no tracking
 * note put it on a vehicle (e.g. baychecked then POD, or closed by a portal
 * PDF) — the real data gap; `progress` is normal for a job not carried yet.
 *
 * Which statuses are which comes from zeecore (`status_kind` on each merged
 * order, from palletwaysJobStatus.ts) — the app keeps no list of its own.
 */
export type MissingVehicleKind = 'progress' | 'gap';

type StatusKindOrder = { status?: string | null; status_kind?: 'progress' | 'done' | null };

function titleCase(status: string): string {
  return status.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Pill colour for a job with no vehicle; null for a status zeecore does not classify. */
export function missingVehicleKind(order: StatusKindOrder): MissingVehicleKind | null {
  if (order.status_kind === 'progress') return 'progress';
  if (order.status_kind === 'done') return 'gap';
  return null;
}

/** The job's status as a Vehicle-cell label; null for an unclassified status (show —, never guess). */
export function missingVehicleLabel(order: StatusKindOrder): string | null {
  return missingVehicleKind(order) ? titleCase(String(order.status ?? '').trim()) : null;
}

export function cancelledOrderStatus(order: {
  status?: string | null;
  vehicle_assigned?: string | null;
  vehicle_observed?: string | null;
  vehicle_pickup?: string | null;
}): CancelledOrderStatus | null {
  const status = String(order.status ?? '').trim().toUpperCase();
  if (status === 'DELETED') return 'DELETED';
  if (status !== 'JOB CLOSED') return null;
  const carried = [order.vehicle_assigned, order.vehicle_observed, order.vehicle_pickup]
    .some((v) => String(v ?? '').trim() !== '');
  return carried ? null : 'JOB CLOSED';
}
